import React, { useEffect, useState, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { executeLaravelFrontAPI, laravelFrontApiUrl, appSecretKey, executeLaravelAPI, userToken } from '../../Utils';
import { Form, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import Cookies from 'js-cookie';
import './ProjectPriceReport.scss';

// Scroll to top helper function - scrolls window and all scrollable containers
const scrollToTop = () => {
    // Scroll window immediately
    window.scrollTo(0, 0);
    
    // Scroll document elements
    if (document.documentElement) {
        document.documentElement.scrollTop = 0;
    }
    if (document.body) {
        document.body.scrollTop = 0;
    }
    
    // Scroll admin layout containers
    const containers = [
        '.content-wrapper',
        '.main-panel',
        '.page-body-wrapper',
        '.container-scroller'
    ];
    
    containers.forEach(selector => {
        const element = document.querySelector(selector);
        if (element) {
            element.scrollTop = 0;
        }
    });
};

const ProjectPriceReport = () => {
    const location = useLocation();
    const [priceData, setPriceData] = useState([]);
    const [loading, setLoading] = useState(true);
    // Get city from cookie (same as Header.js uses)
    const selectedCity = Cookies.get("selectedCityCookie") || "pune";
    const [selectedLocation, setSelectedLocation] = useState('');
    const [locations, setLocations] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
        column: null,
        direction: null // 'asc' or 'desc'
    });
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0
    });
    
    // Always operate in client-side mode: fetch a large dataset once and
    // apply search, sort and pagination on the frontend so RERA search
    // and sorting work across the entire dataset.
    const isClientSideMode = true;

    // Scroll to top immediately before paint (synchronous)
    useLayoutEffect(() => {
        scrollToTop();
    }, [location.pathname]);

    // Scroll to top when component mounts and when route changes
    useEffect(() => {
        // Scroll immediately
        scrollToTop();
        
        // Scroll multiple times to ensure it works
        const scrollAttempts = [
            setTimeout(() => scrollToTop(), 0),
            setTimeout(() => scrollToTop(), 50),
            setTimeout(() => scrollToTop(), 100),
            setTimeout(() => scrollToTop(), 200),
            setTimeout(() => scrollToTop(), 300)
        ];
        
        // Also use requestAnimationFrame for when DOM is ready
        requestAnimationFrame(() => {
            scrollToTop();
            setTimeout(scrollToTop, 50);
            setTimeout(scrollToTop, 100);
        });
        
        return () => {
            scrollAttempts.forEach(timeout => clearTimeout(timeout));
        };
    }, [location.pathname]); // Run when pathname changes (including initial mount)

    // Initial data fetch and city change listener (runs only on mount)
    useEffect(() => {
        document.title = 'Project Price Report - BeyondWalls';
        // Get city from cookie on mount
        const city = Cookies.get("selectedCityCookie") || "pune";
        fetchLocations(city);
        
        // Listen for city change events from Header
        const handleCityChange = (event) => {
            const newCity = event.detail.city || Cookies.get("selectedCityCookie") || "pune";
            // Reset location when city changes
            setSelectedLocation('');
            setPagination(prev => ({ ...prev, current_page: 1 }));
            // Scroll to top when city changes
            scrollToTop();
            setTimeout(scrollToTop, 50);
            setTimeout(scrollToTop, 100);
            // Fetch new data for the new city
            fetchLocations(newCity).then(() => {
                // Trigger price report fetch with new city after locations are loaded
                fetchPriceReport(newCity);
            });
        };
        
        window.addEventListener('cityChanged', handleCityChange);
        
        return () => {
            window.removeEventListener('cityChanged', handleCityChange);
        };
    }, []);

    useEffect(() => {
        // Only fetch from API if NOT in client-side mode (no sorting and no active search)
        // When client-side mode is active, we already have all data and paginate client-side
        if (!isClientSideMode) {
            fetchPriceReport();
        }
    }, [selectedLocation, pagination.current_page, pagination.per_page, isClientSideMode]);

    // Separate effect to fetch all data when client-side mode (sorting or search) is enabled
    useEffect(() => {
        if (isClientSideMode) {
            fetchPriceReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClientSideMode, selectedLocation]);


    const fetchLocations = async (cityName) => {
        try {
            setLoadingLocations(true);
            const city = cityName || Cookies.get("selectedCityCookie") || "pune";
            const params = {
                page: 1,
                per_page: 1000,
                show_all: 1,
                city: city.toLowerCase()
            };
            // Use executeLaravelAPI like Project Media Report (admin API)
            const result = await executeLaravelAPI('locations', params, 'GET', userToken);
            if (result && result.data && result.data.data && Array.isArray(result.data.data)) {
                const locationList = result.data.data.map(loc => ({
                    id: loc.id,
                    name: loc.name,
                    slug: loc.slug
                }));
                setLocations(locationList);
            } else {
                setLocations([]);
            }
            // Reset location filter when city changes
            setSelectedLocation('');
        } catch (error) {
            console.error('Error fetching locations:', error);
            setLocations([]);
        } finally {
            setLoadingLocations(false);
        }
    };

    const fetchPriceReport = async (cityOverride = null) => {
        try {
            setLoading(true);
            // Use cityOverride if provided, otherwise read from cookie
            const city = cityOverride || Cookies.get("selectedCityCookie") || "pune";
            const params = {
                city: city.toLowerCase(),
                // When client-side mode (sorting or search) is active, fetch all data for client-side processing
                // Otherwise, use server-side pagination
                per_page: isClientSideMode ? 10000 : pagination.per_page,
                page: isClientSideMode ? 1 : pagination.current_page,
                bwusercheck: 1
            };
            
            // Add location filter - price-report endpoint uses 'locality' parameter (not locality_name)
            if (selectedLocation) {
                params.locality = selectedLocation;
            }

            // The API endpoint is projects/price-report
            try {
                const result = await executeLaravelFrontAPI('projects/price-report', params, 'GET');
                if (result && result.data && Array.isArray(result.data)) {
                    setPriceData(result.data);
                    if (result.meta && !isClientSideMode) {
                        // Only update pagination from API when NOT in client-side mode
                        setPagination(prev => ({
                            ...prev,
                            current_page: result.meta.current_page || 1,
                            last_page: result.meta.last_page || 1,
                            total: result.meta.total || 0,
                            per_page: result.meta.per_page || prev.per_page
                        }));
                    }
                } else if (result && result.data && result.data.data && Array.isArray(result.data.data)) {
                    setPriceData(result.data.data);
                    if (result.data.meta && !isClientSideMode) {
                        // Only update pagination from API when NOT in client-side mode
                        setPagination(prev => ({
                            ...prev,
                            current_page: result.data.meta.current_page || 1,
                            last_page: result.data.meta.last_page || 1,
                            total: result.data.meta.total || 0,
                            per_page: result.data.meta.per_page || prev.per_page
                        }));
                    }
                } else {
                    setPriceData([]);
                }
            } catch (apiError) {
                // Fallback to direct fetch if executeLaravelFrontAPI fails
                console.warn('executeLaravelFrontAPI failed, trying direct fetch:', apiError);
                
                // Try with /api/v1/ prefix first
                let apiUrl = laravelFrontApiUrl.endsWith('/') 
                    ? `${laravelFrontApiUrl}api/v1/projects/price-report`
                    : `${laravelFrontApiUrl}/api/v1/projects/price-report`;
                
                let urlWithParams = new URL(apiUrl);
                Object.keys(params).forEach((key) => {
                    urlWithParams.searchParams.append(key, params[key]);
                });

                let response = await fetch(urlWithParams.toString(), {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-app-key': appSecretKey || ''
                    }
                });

                // If 404, try without /api/v1/ prefix
                if (response.status === 404) {
                    apiUrl = laravelFrontApiUrl.endsWith('/') 
                        ? `${laravelFrontApiUrl}projects/price-report`
                        : `${laravelFrontApiUrl}/projects/price-report`;
                    urlWithParams = new URL(apiUrl);
                    Object.keys(params).forEach((key) => {
                        urlWithParams.searchParams.append(key, params[key]);
                    });
                    response = await fetch(urlWithParams.toString(), {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'x-app-key': appSecretKey || ''
                        }
                    });
                }

                if (response.status === 200) {
                    const result = await response.json();
                    if (result && result.data && Array.isArray(result.data)) {
                        setPriceData(result.data);
                        if (result.meta && !isClientSideMode) {
                            // Only update pagination from API when NOT in client-side mode
                            setPagination(prev => ({
                                ...prev,
                                current_page: result.meta.current_page || 1,
                                last_page: result.meta.last_page || 1,
                                total: result.meta.total || 0,
                                per_page: result.meta.per_page || prev.per_page
                            }));
                        }
                    } else {
                        setPriceData([]);
                    }
                } else {
                    console.error('API Error:', response.status, response.statusText);
                    setPriceData([]);
                }
            }
        } catch (error) {
            console.error('Error fetching price report:', error);
            setPriceData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleLocationChange = (e) => {
        setSelectedLocation(e.target.value);
        setPagination(prev => ({ ...prev, current_page: 1 }));
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, current_page: newPage }));
    };

    const handlePerPageChange = (e) => {
        setPagination(prev => ({ ...prev, per_page: parseInt(e.target.value, 10), current_page: 1 }));
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSearchClear = () => {
        setSearchTerm('');
    };

    // Export to CSV function
    const exportToCSV = () => {
        try {
            // Use the complete filtered and sorted dataset for export
            const dataToExport = filteredAndSortedData;
            
            if (dataToExport.length === 0) {
                alert('No data to export');
                return;
            }

            // Define CSV headers
            const headers = [
                'Project Name',
                'RERA Number',
                'BHK Type',
                'Carpet Area',
                'Quoted Price',
                'Last Updated Date',
                'Last Update Before'
            ];

            // Convert data to CSV rows
            const csvRows = [];
            
            // Add headers
            csvRows.push(headers.join(','));

            // Add data rows
            dataToExport.forEach(item => {
                const row = [
                    `"${(item.project_name || 'N/A').replace(/"/g, '""')}"`, // Escape quotes in CSV
                    `"${formatReraNumber(item.rera_number).replace(/"/g, '""')}"`,
                    `"${formatBHKType(item.bhk_type).replace(/"/g, '""')}"`,
                    `"${item.carpet_area || 'No'}"`,
                    `"${item.quoted_price || 'No'}"`,
                    `"${item.last_updated_date || 'N/A'}"`,
                    `"${item.last_update_before || 'N/A'}"`
                ];
                csvRows.push(row.join(','));
            });

            // Create CSV content
            const csvContent = csvRows.join('\n');

            // Add BOM for UTF-8 Excel compatibility
            const BOM = '\uFEFF';
            const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

            // Create download link
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            
            // Generate filename with timestamp
            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
            const filename = `project-price-report-${timestamp}.csv`;
            
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error exporting to CSV:', error);
            alert('Error exporting data. Please try again.');
        }
    };

    // Helper function to parse date for sorting
    const parseDateForSort = (dateValue) => {
        if (!dateValue || dateValue === 'N/A') return null;
        
        // Try parsing as date string
        const date = new Date(dateValue);
        if (!isNaN(date.getTime())) {
            return date.getTime();
        }
        
        return null;
    };

    // Helper function to parse "Last Update Before" (e.g., "30 days" -> 30)
    const parseDaysForSort = (daysValue) => {
        if (!daysValue || daysValue === 'N/A') return null;
        
        // Extract number from string like "30 days" or just "30"
        const match = String(daysValue).match(/(\d+)/);
        if (match) {
            return parseInt(match[1], 10);
        }
        
        return null;
    };

    // Handle column sorting
    const handleSort = (column) => {
        let direction = 'asc';
        
        // If clicking the same column, toggle direction
        if (sortConfig.column === column && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        
        setSortConfig({ column, direction });
        // Reset to first page when sorting changes
        setPagination(prev => ({ ...prev, current_page: 1 }));
    };

    // Filter and sort price data
    const filteredAndSortedData = React.useMemo(() => {
        let data = [...priceData]; // Create a copy to avoid mutating original
        
        // Apply search filter
        if (searchTerm && searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase().trim();
            data = data.filter(item => {
                // Helper function to format RERA number for comparison
                const formatReraForSearch = (reraNumber) => {
                    if (!reraNumber) return '';
                    try {
                        if (typeof reraNumber === 'string') {
                            const parsed = JSON.parse(reraNumber.replace(/'/g, '"'));
                            if (Array.isArray(parsed) && parsed.length > 0) {
                                return String(parsed[0]);
                            }
                        }
                        return String(reraNumber);
                    } catch (error) {
                        return String(reraNumber);
                    }
                };
                
                const reraNumber = formatReraForSearch(item.rera_number);
                if (!reraNumber) return false;
                
                const reraLower = reraNumber.toLowerCase();
                return reraLower.includes(searchLower);
            });
        }
        
        // Apply sorting to entire dataset
        if (sortConfig.column) {
            data.sort((a, b) => {
                let aValue = null;
                let bValue = null;
                
                if (sortConfig.column === 'last_updated_date') {
                    aValue = parseDateForSort(a.last_updated_date);
                    bValue = parseDateForSort(b.last_updated_date);
                } else if (sortConfig.column === 'last_update_before') {
                    aValue = parseDaysForSort(a.last_update_before);
                    bValue = parseDaysForSort(b.last_update_before);
                } else if (sortConfig.column === 'carpet_area') {
                    // Sort by Yes/No: Yes = 0 (comes first in asc), No = 1 (comes later)
                    // This way ascending shows Yes first, descending shows No first
                    aValue = (a.carpet_area === 'Yes') ? 0 : 1;
                    bValue = (b.carpet_area === 'Yes') ? 0 : 1;
                } else if (sortConfig.column === 'quoted_price') {
                    // Sort by Yes/No: Yes = 0 (comes first in asc), No = 1 (comes later)
                    // This way ascending shows Yes first, descending shows No first
                    aValue = (a.quoted_price === 'Yes') ? 0 : 1;
                    bValue = (b.quoted_price === 'Yes') ? 0 : 1;
                }
                
                // Handle null values (push to end) - only for date/days columns
                if (sortConfig.column === 'last_updated_date' || sortConfig.column === 'last_update_before') {
                    if (aValue === null && bValue === null) return 0;
                    if (aValue === null) return 1;
                    if (bValue === null) return -1;
                }
                
                // Sort based on direction
                if (sortConfig.direction === 'asc') {
                    return aValue - bValue;
                } else {
                    return bValue - aValue;
                }
            });
        }
        
        return data;
    }, [priceData, searchTerm, sortConfig]);

    // Apply client-side pagination when client-side mode (sorting or search) is active
    const filteredPriceData = React.useMemo(() => {
        if (isClientSideMode) {
            // Client-side pagination for filtered/sorted data
            const startIndex = (pagination.current_page - 1) * pagination.per_page;
            const endIndex = startIndex + pagination.per_page;
            return filteredAndSortedData.slice(startIndex, endIndex);
        }
        // When not in client-side mode, return filtered data as-is (server-side pagination)
        return filteredAndSortedData;
    }, [filteredAndSortedData, isClientSideMode, pagination.current_page, pagination.per_page]);

    // Update pagination when client-side mode filtering/sorting changes the data length
    useEffect(() => {
        if (isClientSideMode) {
            const totalItems = filteredAndSortedData.length;
            const calculatedLastPage = Math.max(1, Math.ceil(totalItems / pagination.per_page));
            
            setPagination(prev => {
                // Only update if the calculated values are different or if we need to reset current_page
                if (prev.total !== totalItems || prev.last_page !== calculatedLastPage || prev.current_page > calculatedLastPage) {
                    return {
                        ...prev,
                        total: totalItems,
                        last_page: calculatedLastPage,
                        current_page: prev.current_page > calculatedLastPage ? 1 : prev.current_page
                    };
                }
                return prev;
            });
        }
    }, [filteredAndSortedData.length, sortConfig.column, pagination.per_page]);

    const formatReraNumber = (reraNumber) => {
        if (!reraNumber) return 'N/A';
        try {
            if (typeof reraNumber === 'string') {
                const parsed = JSON.parse(reraNumber.replace(/'/g, '"'));
                if (Array.isArray(parsed) && parsed.length > 0) {
                    return parsed[0];
                }
            }
            return reraNumber;
        } catch (error) {
            return reraNumber;
        }
    };

    const formatBHKType = (bhkType) => {
        if (!bhkType) return 'No / -';
        return bhkType;
    };

    return (
        <div className="project-price-report">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="page-header mb-4">
                            <h2 className="page-title">Project Price Report</h2>
                            <p className="text-muted">View and filter project price information by city and location</p>
                        </div>

                        {/* Filters */}
                        <div className="filters-section mb-4">
                            {/* Search Bar */}
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <Form.Group>
                                        <Form.Label>Search by RERA No.</Form.Label>
                                        <div className="position-relative">
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter RERA number to search"
                                                value={searchTerm}
                                                onChange={handleSearchChange}
                                            />
                                            {searchTerm && (
                                                <button
                                                    type="button"
                                                    className="btn btn-link position-absolute"
                                                    style={{ right: '5px', top: '50%', transform: 'translateY(-50%)', padding: '0', zIndex: 10 }}
                                                    onClick={handleSearchClear}
                                                >
                                                    <Icon icon="mdi:close-circle" style={{ fontSize: '20px', color: '#6c757d' }} />
                                                </button>
                                            )}
                                        </div>
                                    </Form.Group>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-4">
                                    <Form.Group>
                                        <Form.Label>Location</Form.Label>
                                        <Form.Select
                                            value={selectedLocation}
                                            onChange={handleLocationChange}
                                            disabled={loadingLocations || locations.length === 0}
                                        >
                                            <option value="">All Locations</option>
                                            {locations.map((location) => (
                                                <option key={location.slug} value={location.name}>
                                                    {location.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group>
                                        <Form.Label>Per Page</Form.Label>
                                        <Form.Select
                                            value={pagination.per_page}
                                            onChange={handlePerPageChange}
                                        >
                                            <option value="20">20</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                            <option value="500">500</option>
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                <div className="col-md-4 d-flex align-items-end justify-content-end gap-2">
                                    <div className="text-muted me-3">
                                        {loading ? (
                                            <span>Loading...</span>
                                        ) : (
                                            <span>
                                                Total: <strong>
                                                    {sortConfig.column 
                                                        ? filteredAndSortedData.length 
                                                        : (searchTerm ? filteredPriceData.length : pagination.total)
                                                    }
                                                </strong>
                                            </span>
                                        )}
                                    </div>
                                    <Button 
                                        variant="success" 
                                        onClick={exportToCSV}
                                        disabled={loading || filteredAndSortedData.length === 0}
                                    >
                                        <Icon icon="mdi:download" className="me-2" />
                                        Export CSV
                                    </Button>
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col-12">
                                    <p className="text-muted mb-0">
                                        <small>Note: City selection is available in the header dropdown above.</small>
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Table */}
                        <div className={`table-responsive ${loading ? 'loading-state' : ''}`}>
                            <table className="table table-striped table-hover price-report-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <div className="d-flex align-items-center">
                                                Project name
                                                <Icon icon="mdi:arrow-up-down" className="ms-2 sort-icon" />
                                            </div>
                                        </th>
                                        <th>
                                            <div className="d-flex align-items-center">
                                                Rera number
                                                <Icon icon="mdi:arrow-up-down" className="ms-2 sort-icon" />
                                            </div>
                                        </th>
                                        <th>
                                            <div className="d-flex align-items-center">
                                                BHK Type
                                                <Icon icon="mdi:arrow-up-down" className="ms-2 sort-icon" />
                                            </div>
                                        </th>
                                        <th>
                                            <div 
                                                className="d-flex align-items-center cursor-pointer"
                                                onClick={() => handleSort('carpet_area')}
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                            >
                                                Carpet Area
                                                {sortConfig.column === 'carpet_area' ? (
                                                    <>
                                                        <Icon 
                                                            icon={sortConfig.direction === 'asc' ? "mdi:arrow-up" : "mdi:arrow-down"} 
                                                            className="ms-2 sort-icon" 
                                                            style={{ color: '#007bff' }}
                                                        />
                                                        <span 
                                                            className="ms-1 badge bg-info" 
                                                            style={{ fontSize: '0.7rem', fontWeight: 'normal' }}
                                                        >
                                                            {sortConfig.direction === 'asc' ? 'Yes' : 'No'}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <Icon icon="mdi:arrow-up-down" className="ms-2 sort-icon" style={{ opacity: 0.3 }} />
                                                )}
                                            </div>
                                        </th>
                                        <th>
                                            <div 
                                                className="d-flex align-items-center cursor-pointer"
                                                onClick={() => handleSort('quoted_price')}
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                            >
                                                Quoted Price
                                                {sortConfig.column === 'quoted_price' ? (
                                                    <>
                                                        <Icon 
                                                            icon={sortConfig.direction === 'asc' ? "mdi:arrow-up" : "mdi:arrow-down"} 
                                                            className="ms-2 sort-icon" 
                                                            style={{ color: '#007bff' }}
                                                        />
                                                        <span 
                                                            className="ms-1 badge bg-info" 
                                                            style={{ fontSize: '0.7rem', fontWeight: 'normal' }}
                                                        >
                                                            {sortConfig.direction === 'asc' ? 'Yes' : 'No'}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <Icon icon="mdi:arrow-up-down" className="ms-2 sort-icon" style={{ opacity: 0.3 }} />
                                                )}
                                            </div>
                                        </th>
                                        <th>
                                            <div 
                                                className="d-flex align-items-center cursor-pointer"
                                                onClick={() => handleSort('last_updated_date')}
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                            >
                                                Last Updated Date
                                                {sortConfig.column === 'last_updated_date' ? (
                                                    <>
                                                        <Icon 
                                                            icon={sortConfig.direction === 'asc' ? "mdi:arrow-up" : "mdi:arrow-down"} 
                                                            className="ms-2 sort-icon" 
                                                            style={{ color: '#007bff' }}
                                                        />
                                                        <span 
                                                            className="ms-1 badge bg-info" 
                                                            style={{ fontSize: '0.7rem', fontWeight: 'normal' }}
                                                        >
                                                            {sortConfig.direction === 'asc' ? 'Oldest' : 'Newest'}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <Icon icon="mdi:arrow-up-down" className="ms-2 sort-icon" style={{ opacity: 0.3 }} />
                                                )}
                                            </div>
                                        </th>
                                        <th>
                                            <div 
                                                className="d-flex align-items-center cursor-pointer"
                                                onClick={() => handleSort('last_update_before')}
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                            >
                                                Last Update Before
                                                {sortConfig.column === 'last_update_before' ? (
                                                    <>
                                                        <Icon 
                                                            icon={sortConfig.direction === 'asc' ? "mdi:arrow-up" : "mdi:arrow-down"} 
                                                            className="ms-2 sort-icon" 
                                                            style={{ color: '#007bff' }}
                                                        />
                                                        <span 
                                                            className="ms-1 badge bg-info" 
                                                            style={{ fontSize: '0.7rem', fontWeight: 'normal' }}
                                                        >
                                                            {sortConfig.direction === 'asc' ? 'Fewest' : 'Most'}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <Icon icon="mdi:arrow-up-down" className="ms-2 sort-icon" style={{ opacity: 0.3 }} />
                                                )}
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className={loading ? 'skeleton-loading' : ''}>
                                    {loading ? (
                                        // Skeleton loader rows - show more rows to maintain height and prevent footer jump
                                        Array.from({ length: 10 }).map((_, index) => (
                                            <tr key={`skeleton-${index}`} className="skeleton-row" style={{ height: '48px' }}>
                                                <td style={{ height: '48px', padding: '12px 15px' }}>
                                                    <div className="skeleton-loader" style={{ width: '60%', height: '16px', marginTop: '8px', minHeight: '16px' }}></div>
                                                </td>
                                                <td style={{ height: '48px', padding: '12px 15px' }}>
                                                    <div className="skeleton-loader" style={{ width: '70%', height: '16px', marginTop: '8px', minHeight: '16px' }}></div>
                                                </td>
                                                <td style={{ height: '48px', padding: '12px 15px' }}>
                                                    <div className="skeleton-loader" style={{ width: '50%', height: '16px', marginTop: '8px', minHeight: '16px' }}></div>
                                                </td>
                                                <td style={{ height: '48px', padding: '12px 15px' }}>
                                                    <div className="skeleton-loader" style={{ width: '40%', height: '24px', borderRadius: '12px', margin: '0 auto', marginTop: '4px', minHeight: '24px' }}></div>
                                                </td>
                                                <td style={{ height: '48px', padding: '12px 15px' }}>
                                                    <div className="skeleton-loader" style={{ width: '40%', height: '24px', borderRadius: '12px', margin: '0 auto', marginTop: '4px', minHeight: '24px' }}></div>
                                                </td>
                                                <td style={{ height: '48px', padding: '12px 15px' }}>
                                                    <div className="skeleton-loader" style={{ width: '60%', height: '16px', marginTop: '8px', minHeight: '16px' }}></div>
                                                </td>
                                                <td style={{ height: '48px', padding: '12px 15px' }}>
                                                    <div className="skeleton-loader" style={{ width: '50%', height: '16px', marginTop: '8px', minHeight: '16px' }}></div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : filteredPriceData.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-5">
                                                {searchTerm ? 'No matching RERA number found' : 'No data found'}
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredPriceData.map((item, index) => (
                                            <tr key={item.project_id || index}>
                                                <td>{item.project_name || 'N/A'}</td>
                                                <td>{formatReraNumber(item.rera_number)}</td>
                                                <td>{formatBHKType(item.bhk_type)}</td>
                                                <td>
                                                    <span className={`badge ${item.carpet_area === 'Yes' ? 'bg-success' : 'bg-secondary'}`}>
                                                        {item.carpet_area || 'No'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`badge ${item.quoted_price === 'Yes' ? 'bg-success' : 'bg-secondary'}`}>
                                                        {item.quoted_price || 'No'}
                                                    </span>
                                                </td>
                                                <td>{item.last_updated_date || 'N/A'}</td>
                                                <td>{item.last_update_before || 'N/A'}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {!loading && pagination.last_page > 1 && (
                            <div className="pagination-section mt-4">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="pagination-info">
                                        {(() => {
                                            const totalEntries = sortConfig.column ? filteredAndSortedData.length : pagination.total;
                                            const startEntry = ((pagination.current_page - 1) * pagination.per_page) + 1;
                                            const endEntry = Math.min(pagination.current_page * pagination.per_page, totalEntries);
                                            return `Showing ${startEntry} to ${endEntry} of ${totalEntries} entries`;
                                        })()}
                                    </div>
                                    <nav>
                                        <ul className="pagination mb-0">
                                            <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link" 
                                                    onClick={() => handlePageChange(pagination.current_page - 1)}
                                                    disabled={pagination.current_page === 1}
                                                >
                                                    Previous
                                                </button>
                                            </li>
                                            {[...Array(pagination.last_page)].map((_, index) => {
                                                const page = index + 1;
                                                // Show first page, last page, current page, and pages around current
                                                if (
                                                    page === 1 ||
                                                    page === pagination.last_page ||
                                                    (page >= pagination.current_page - 2 && page <= pagination.current_page + 2)
                                                ) {
                                                    return (
                                                        <li key={page} className={`page-item ${pagination.current_page === page ? 'active' : ''}`}>
                                                            <button 
                                                                className="page-link" 
                                                                onClick={() => handlePageChange(page)}
                                                            >
                                                                {page}
                                                            </button>
                                                        </li>
                                                    );
                                                } else if (
                                                    page === pagination.current_page - 3 ||
                                                    page === pagination.current_page + 3
                                                ) {
                                                    return (
                                                        <li key={page} className="page-item disabled">
                                                            <span className="page-link">...</span>
                                                        </li>
                                                    );
                                                }
                                                return null;
                                            })}
                                            <li className={`page-item ${pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link" 
                                                    onClick={() => handlePageChange(pagination.current_page + 1)}
                                                    disabled={pagination.current_page === pagination.last_page}
                                                >
                                                    Next
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectPriceReport;
